import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, ScriptKicker, FilipinoSun } from "../components/Reveal";
import { EVENTS } from "../lib/data";
import { usePageMeta } from "../hooks/usePageMeta";

export default function Catering() {
  usePageMeta({
    title: "Filipino Catering San Diego | Ateng K Sarap Catering",
    description:
      "Looking for the best Filipino caterer in San Diego? Ateng K Sarap offers budget-friendly catering, delivery & private event service. Get a free quote!",
    path: "/catering",
  });
  return (
    <div data-testid="catering-page" className="overflow-hidden">
      <section className="relative pt-20 pb-16 lg:pt-32 lg:pb-24 bg-background">
        <div className="absolute top-16 right-10 text-accent/25 hidden lg:block"><FilipinoSun size={140} /></div>
        <div className="container-eds relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="overline flex items-center gap-3"><span className="w-8 h-px bg-secondary" /> Catering Events</div>
            <ScriptKicker className="block mt-3">Big love, small details</ScriptKicker>
            <h1 className="mt-2 font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-medium text-foreground leading-[0.98] tracking-tight">
              Tailored Filipino <span className="italic text-secondary">catering</span>
              <br />for every <span className="italic text-primary">celebration</span>.
            </h1>
          </div>
          <div className="lg:col-span-4 text-muted-foreground text-base lg:text-lg">
            From intimate dinners to grand fiestas — we build menus, service,
            and presentation around your event.
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 lg:py-28 bg-card">
        <div className="container-eds">
          <Reveal>
            <div className="text-center mx-auto max-w-3xl">
              <div className="overline flex items-center justify-center gap-3"><span className="w-8 h-px bg-secondary" /> How It Works <span className="w-8 h-px bg-secondary" /></div>
              <ScriptKicker className="block mt-3">Simple. Warm. Done right.</ScriptKicker>
              <h2 className="mt-2 font-display text-4xl lg:text-5xl xl:text-6xl font-medium text-foreground leading-tight">
                From inquiry to <span className="italic text-secondary">celebration</span>.
              </h2>
            </div>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Inquire", b: "Tell us about your event, guest count, and budget." },
              { n: "02", t: "Custom Menu", b: "We design a Filipino menu tailored to your celebration." },
              { n: "03", t: "Confirm", b: "Lock in details one week before your event date." },
              { n: "04", t: "Celebrate", b: "We deliver, set up, and let you enjoy the moment." },
            ].map((s, i) => {
              const offsets = ["", "md:translate-y-6", "lg:translate-y-12", "lg:translate-y-6"];
              return (
                <Reveal key={s.n} delay={i * 0.08} className={offsets[i]}>
                  <div className="bg-background border border-border rounded-3xl p-7 h-full hover:bg-foreground hover:text-background transition-all duration-500 group">
                    <div className="font-display text-6xl text-accent font-medium leading-none">{s.n}</div>
                    <h3 className="mt-5 font-display text-2xl font-semibold">{s.t}</h3>
                    <p className="mt-2 text-sm text-muted-foreground group-hover:text-background/80 leading-relaxed">{s.b}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Events */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container-eds">
          <Reveal>
            <div className="overline flex items-center gap-3"><span className="w-8 h-px bg-secondary" /> Events We Cater</div>
            <ScriptKicker className="block mt-3">From the intimate to the grand</ScriptKicker>
            <h2 className="mt-2 font-display text-4xl lg:text-5xl xl:text-6xl font-medium text-foreground leading-tight max-w-3xl">
              Built for every kind of <span className="italic text-primary">celebration</span>.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-12 gap-5">
            {EVENTS.map((e, i) => {
              const layouts = [
                "col-span-12 md:col-span-7 lg:col-span-5",
                "col-span-12 md:col-span-5 lg:col-span-4 md:translate-y-12",
                "col-span-12 md:col-span-6 lg:col-span-3",
                "col-span-12 md:col-span-6 lg:col-span-4",
                "col-span-12 md:col-span-7 lg:col-span-5 lg:translate-y-6",
                "col-span-12 md:col-span-5 lg:col-span-3",
              ];
              return (
                <Reveal key={e.title} delay={i * 0.06} className={layouts[i]}>
                  <div className="group relative overflow-hidden rounded-3xl h-[380px]">
                    <img src={e.image} alt={e.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/95 via-foreground/40 to-transparent" />
                    <div className="absolute inset-0 p-7 flex flex-col justify-end text-background">
                      <div className="font-script text-2xl text-accent">No {String(i + 1).padStart(2, "0")}</div>
                      <h3 className="font-display text-3xl font-medium mt-1">{e.title}</h3>
                      <p className="mt-2 text-sm text-background/85">{e.blurb}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-20 lg:py-28 bg-card">
        <div className="container-eds grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-6">
            <div className="polaroid rotate-l1">
              <img
                src="/images/event-food.webp"
                alt="Catering setup"
                loading="lazy"
                decoding="async"
                className="w-full h-[520px] object-cover"
              />
              <div className="polaroid-caption">Setup at your venue</div>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-6" delay={0.1}>
            <div className="overline">What's Included</div>
            <ScriptKicker className="block mt-2">All you need, none of the stress</ScriptKicker>
            <h2 className="mt-2 font-display text-4xl lg:text-5xl font-medium text-foreground leading-tight">
              Full-service Filipino <span className="italic text-secondary">catering</span>.
            </h2>
            <ul className="mt-8 space-y-4">
              {[
                "Custom-designed menu based on your budget & guests",
                "Authentic Filipino dishes prepared the day of your event",
                "Professional setup, chafing dishes, and serving ware",
                "Friendly hospitality service from arrival to clean-up",
                "Vegetarian, gluten-free, and dietary accommodations available",
                "Beautiful presentation worthy of your celebration",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <div className="mt-0.5 w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-primary-foreground" />
                  </div>
                  <span className="text-foreground text-base">{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/contact" data-testid="catering-inquiry-btn" className="btn-secondary mt-10">
              Start Your Catering Inquiry <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-5 text-xs text-muted-foreground italic font-script text-base">
              * Orders must be placed one week prior to your event.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
